<script>
export default {
  name: "PrivacyAgreementCheckBox",
  props: {
    value: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      checked: this.value,
      show: false
    }
  },
  watch: {
    checked(val) {
      this.$emit('input', val)
    }
  }
}
</script>

<template>
<div class="agreement-check-wrap">
  <el-checkbox v-model="checked" />
  <span>{{ this.$t('login["I have read and agree"]') }}</span>
  <span class="link" @click.prevent.stop="show = !show">《{{ this.$t('login["Privacy Agreement"]') }}》</span>
  <div class="privacy-agreement-container" v-show="show">
    <article class="privacy-agreement-wrap">
      <h1>Consumer Business Privacy Statement</h1>
      <h2>Introduction</h2>
      <p>The website and its global affiliates (collectively referred to as "the website," "we," or "our") value your privacy. We have developed this Privacy Statement (hereinafter referred to as "this Statement") to help you understand how we collect, use, disclose, protect, store, and transmit your personal data. Please read this Statement carefully and feel free to contact us if you have any questions.</p>
      <p>This Statement applies to all website products and services that display or mention this Statement, including <span class="bold">QR tags</span>, <span class="bold">software</span>, <span class="bold">toolkits</span>, <span class="bold">websites</span>, and <span class="bold">other services</span>.</p>
      <p>In addition, we recommend that you carefully read the Privacy Statement or supplementary notice related to specific products or services (hereinafter referred to as the "Product Privacy Notice"). The Product Privacy Notice is an integral part of this Statement and provides detailed descriptions on how each product or service processes your personal data. If any conflicts arise between this Statement and the Product Privacy Notice, the latter shall prevail.</p>
      <hr />
      <h3>1. How the Website Collects Your Personal Data</h3>
      <h4>1.1 Definition of Personal Data</h4>
      <p>Personal data refers to any information related to an identified or identifiable natural person, excluding anonymized data. When you use our products or services or interact with us, we may collect your personal data. The types of data collected will vary based on the specific service and your interactions with us. In certain cases, you may choose not to provide this data, but this may affect the normal use of certain services or result in us being unable to process your request.</p>
      <p>We may also collect statistical data (such as visit frequency, code request frequency, and product quantities) to analyze how our products and services are used. Such statistical data is considered non-personal data. However, if non-personal data is mixed with personal data, we will treat the entire dataset as personal data.</p>
      <hr />
      <h4>1.2 Types of Personal Data We May Collect</h4>
      <h5>(1) Personal Data Provided by You</h5>
      <ul>
        <li>
          <div><span class="bold">Account Registration and Service Usage:</span><br />When you register or log in to your website ID to add or delete tags or purchase services, we may collect your name, email address, mobile number, and other information.</div>
        </li>
      </ul>
      <p> </p>
      <ul>
        <li>
          <div><span class="bold">Interaction and Sharing with Others:</span><br />Some products allow you to communicate and share content with others. When using these features, you may need to create a public user profile that includes your nickname and avatar. We will also collect the contact information you provide (such as email addresses and phone numbers) to ensure communication security.</div>
        </li>
      </ul>
      <p> </p>
      <ul>
        <li>
          <div><span class="bold">Identity Verification and Payment Requirements:</span><br />To comply with local laws, such as those for real-name registration, anti-addiction systems, and online payments, we may need to collect government-issued identity proof or card information to verify your identity.</div>
        </li>
      </ul>
      <p> </p>
      <h5>(2) Data Collected Automatically During Service Use</h5>
      <ul>
        <li>
          <div><span class="bold">Device and Account Information:</span> This includes device name, device identifier, activation time, hardware model, OS version, app version, region, and language settings.</div>
        </li>
        <li>
          <div><span class="bold">Log Information:</span> Includes access time, access count, IP address, error and crash logs, and other log information.</div>
        </li>
        <li>
          <div><span class="bold">Location Information:</span> When you scan a QR tag, we may collect location information from your device (via GPS). You may disable location permissions in your device settings.</div>
        </li>
      </ul>
      <hr />
      <h3>2. How We Use Your Personal Data</h3>
      <p>We will use your personal data based on legally permissible grounds. The purposes include, but are not limited to:</p>
      <ol>
        <li>
          <div>Registering and activating products or services you have purchased;</div>
        </li>
        <li>
          <div>Providing a personalized user experience, with an option for you to decline personalization;</div>
        </li>
        <li>
          <div>Sending marketing information or invitations to promotional events after obtaining your consent;</div>
        </li>
        <li>
          <div>Conducting internal data analysis, business research, and auditing;</div>
        </li>
        <li>
          <div>Improving our loss prevention and anti-fraud programs;</div>
        </li>
        <li>
          <div>Displaying your contact information (such as phone number) when someone scans your QR tag.</div>
        </li>
      </ol>
      <hr />
      <h3>3. Cookies and Similar Technologies</h3>
      <p>We may use cookies, pixel tags, and local storage technologies to enhance your experience.</p>
      <ul>
        <li>
          <div><span class="bold">Necessary Cookies:</span> Help users log in and save preferences.</div>
        </li>
        <li>
          <div><span class="bold">Analytical Cookies:</span> Used for statistical analysis of user behavior.</div>
        </li>
        <li>
          <div><span class="bold">Advertising Cookies:</span> Provide personalized advertisements based on your interests.</div>
        </li>
      </ul>
      <p>You can manage or delete cookies through your browser, but disabling cookies may affect the functionality of certain services.</p>
      <hr />
      <h3>4. How We Entrust, Share, Transfer, and Disclose Your Personal Data</h3>
      <h4>4.1 Entrustment</h4>
      <p>We may entrust third parties to provide services such as customer support or technical support. These third parties can only process data under our supervision and in accordance with our instructions. They are required to implement confidentiality measures and must not retain this data after the entrustment contract ends.</p>
      <h4>4.2 Sharing</h4>
      <p>We will only share your data with third parties under the following circumstances:</p>
      <ol>
        <li>
          <div>With your consent, sharing with designated third parties;</div>
        </li>
        <li>
          <div>When legally required, sharing with regulatory agencies;</div>
        </li>
        <li>
          <div>Sharing with business partners: For instance, sharing order information with logistics companies for delivery purposes.</div>
        </li>
      </ol>
      <h4>4.3 Data Transfer</h4>
      <p>In the event of company mergers, divisions, or bankruptcy involving data transfer, we will notify you in advance and obtain your consent if necessary.</p>
      <h4>4.4 Public Disclosure</h4>
      <p>We will only publicly disclose your personal data with your consent or when required by law.</p>
      <hr />
      <h3>5. Managing Your Personal Data</h3>
      <p>You have the right to access, correct, or delete your personal data, and may withdraw your consent for data processing at any time. To exercise these rights, please contact our customer service channels.</p>
      <hr />
      <h3>6. Cross-Border Data Transfers</h3>
      <p>As a global company, your data may be processed in different countries or regions. We ensure that data is processed in accordance with this Statement and applicable laws.</p>
      <hr />
      <h3>7. Updates to This Statement</h3>
      <p>We reserve the right to update this Statement at any time. For major changes, we will notify you via a website notice or direct communication.</p>
      <hr />
      <h3>8. Contact Us</h3>
      <p>If you have any questions or suggestions, please contact us via online customer service or email at service@petheylife.com. We will respond to your request within the time frame specified by law.</p>
      <hr />
      <h3>Copyright Statement</h3>
      <p>Copyright &copy; The Website. All rights reserved.<br />Last Updated: October 23, 2024</p>
    </article>
    <div class="button-container">
      <el-button type="primary" @click="() => {checked = true; show = false}">I Agree</el-button>
      <el-button type="info" @click="() => {checked = false; show = false}">I Don't Agree</el-button>
    </div>
  </div>
</div>
</template>

<style scoped>
.agreement-check-wrap {
  margin: 20px 0;
  text-align: center;
}

.el-checkbox {
  margin-right: 5px;
}

.privacy-agreement-container {
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  padding: 20px 20px 60px;
  background-color: white;
  overflow-y: scroll;
  z-index: 10;
}
.button-container {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 10px 0;
  background-color: white;
  box-shadow: 0 0 6px rgba(0, 0, 0, 0.2);
  text-align: center;
}

.privacy-agreement-wrap h1 {
  font-size: 1.5em;
}
.privacy-agreement-wrap h2 {
  font-size: 1.4em;
}
.privacy-agreement-wrap h3 {
  font-size: 1.3em;
}
.privacy-agreement-wrap h4 {
  font-size: 1.2em;
}
.privacy-agreement-wrap h5 {
  font-size: 1.1em;
}
.privacy-agreement-wrap {
  text-align: left;
}
.privacy-agreement-wrap .bold {
  font-weight: bold;
}

.link {
  display: inline-block;
  color: #3C8BF2;
}
</style>



// WEBPACK FOOTER //
// src/components/PrivacyAgreementCheckBox.vue